import { useEffect, useRef, useState } from "react";
import { PricingNotReadyErrorType } from "@covertree/types";
import { useGetPolicy } from "./useGetPolicy";

const RETRY_INTERVAL_MS = 4_000;
const MAX_RETRY_DURATION_MS = 30_000;

function isRetriablePricingError(error: unknown): boolean {
  const graphQLErrors = (error as { graphQLErrors?: { errorType?: string }[] })
    ?.graphQLErrors;
  return !!graphQLErrors?.some(
    ({ errorType }) =>
      errorType === PricingNotReadyErrorType.PricingNotReadyError
  );
}

/**
 * Flat retry. Reports `loading` (and hides the error) while retrying.
 * Stops after MAX_RETRY_DURATION_MS and returns error.
 */
export function useGetPolicyWithPricingRetry(
  policyLocator: string,
  canMarkRenewalAsPaid: boolean
) {
  const queryResult = useGetPolicy(policyLocator, canMarkRenewalAsPaid);

  const [timedOutFor, setTimedOutFor] = useState<string | null>(null);

  const refetchRef = useRef(queryResult.refetch);
  refetchRef.current = queryResult.refetch;

  const retriableError = isRetriablePricingError(queryResult.error);
  const policy = queryResult.data?.getPolicy;
  const nonRetriableError = !!queryResult.error && !retriableError;
  const pricingMissing =
    !queryResult.loading &&
    !nonRetriableError &&
    (retriableError || (!!policy && policy.units[0]?.pricing == null));

  const timedOut = timedOutFor === policyLocator;
  const isPricingPending = pricingMissing && !timedOut;

  useEffect(() => {
    if (!isPricingPending) {
      return;
    }

    const interval = setInterval(() => {
      refetchRef.current().catch(() => undefined);
    }, RETRY_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isPricingPending]);

  useEffect(() => {
    if (!isPricingPending) {
      return;
    }

    const timeout = setTimeout(
      () => setTimedOutFor(policyLocator),
      MAX_RETRY_DURATION_MS
    );

    return () => clearTimeout(timeout);
  }, [isPricingPending, policyLocator]);

  return {
    data: queryResult.data,
    loading: queryResult.loading || isPricingPending,
    error: isPricingPending
      ? undefined
      : timedOut
        ? queryResult.error ?? new Error("Something went wrong")
        : queryResult.error,
    refetch: queryResult.refetch,
  };
}
